import React from 'react';
import { Button, GetColor, Link as LinkStyle, Notifications, NotificationType, Subtitle2, Warning } from 'venn-ui-kit';
import styled from 'styled-components';
import { analyticsService, useHasFF, useRedrawCharts } from 'venn-utils';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { hasChangedLayoutState, isReportState, LayoutVersion, layoutVersionState } from 'venn-state';
import { Text } from '../../../shared';

export const FontSizeUpgradeSection: React.ComponentType = ({ children }) => {
  const [layoutVersion, setLayoutVersion] = useRecoilState(layoutVersionState);
  const [hasChangedLayout, setHasChangedLayout] = useRecoilState(hasChangedLayoutState);
  const isReport = useRecoilValue(isReportState);
  const hasModernLayoutNoRevertFF = useHasFF('modern_layout_no_revert_ff');
  const hasModernLayoutFF = useHasFF('modern_layout_ff');
  useRedrawCharts([layoutVersion]);

  if (!hasModernLayoutFF || !isReport) {
    return <>{children}</>;
  }

  const onVersionUpgrade = () => {
    setLayoutVersion(LayoutVersion.MODERN);
    setHasChangedLayout(true);
    Notifications.notifyPersistent(
      'Font sizes successfully updated! You may need to adjust page layouts to accommodate.',
      NotificationType.SUCCESS,
    );
    analyticsService.ctaClicked({
      purpose: 'Upgrade report lab font size',
      text: 'Update all font sizes',
      locationOnPage: 'Report lab left panel',
    });
  };

  return layoutVersion === LayoutVersion.LEGACY ? (
    <>
      <Wrapper>
        <FlexWrapper>
          <Subtitle2>Font Standardization</Subtitle2>
          <Pill>New</Pill>
        </FlexWrapper>
        <Text>
          Venn has now standardized font sizes across all new reports. Click the button below to update this report.
        </Text>
        <ButtonWrapper>
          <Button dense icon="text-size" onClick={onVersionUpgrade}>
            Update all font sizes
          </Button>
        </ButtonWrapper>
        {hasModernLayoutNoRevertFF ? (
          <Warning
            text="After updating, you will no longer be able to revert this report to its previous font sizes. All reports
            will automatically update on 1 November 2023."
          />
        ) : (
          <Text>
            You may revert these changes to suit your current reporting needs. Please don’t hesitate to contact us with
            any questions.
          </Text>
        )}
      </Wrapper>
      {children}
    </>
  ) : !hasModernLayoutNoRevertFF ? (
    hasChangedLayout ? (
      <>
        <Wrapper>
          <FlexWrapper>
            <Subtitle2>Font Standardization</Subtitle2>
            <Pill>New</Pill>
          </FlexWrapper>
          <RevertUpdateComponent />
        </Wrapper>
        {children}
      </>
    ) : (
      <>
        {children}
        <FooterWrapper>
          <RevertUpdateComponent />
        </FooterWrapper>
      </>
    )
  ) : (
    <>{children}</>
  );
};

const RevertUpdateComponent = () => {
  const setLayoutVersion = useSetRecoilState(layoutVersionState);

  return (
    <Text>
      Font sizes have been standardized for this report.{' '}
      <button
        type="button"
        onClick={() => {
          setLayoutVersion(LayoutVersion.LEGACY);
          analyticsService.ctaClicked({
            purpose: 'Revert report lab font size',
            text: 'Revert update',
            locationOnPage: 'Report lab left panel',
          });
        }}
      >
        <LinkStyle>Revert update</LinkStyle>
      </button>
    </Text>
  );
};

const ButtonWrapper = styled.div`
  padding: 8px 0;
  > button {
    width: 100%;
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Pill = styled.span`
  font-size: 9px;
  font-weight: bold;
  padding: 3px 4px;
  border-radius: 4px;
  background-color: ${GetColor.HighlightDark};
  color: ${GetColor.White};
  text-transform: uppercase;
`;

const Wrapper = styled.div`
  padding: 15px;
  border-bottom: 1px solid ${GetColor.Grey};
`;

const FooterWrapper = styled.div`
  padding: 15px;
  border-top: 1px solid ${GetColor.Grey};
`;
